import { Col, Row, useToggle } from '@met/react-components';
import React, { Fragment, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PageHeader } from '../../components';
import history from '../../history';
import {
  languagesActions,
  namespacesActions,
  projectsActions,
} from '../../store';
import AddLanguageModal from './components/AddLanguageModal';
import AddNamespaceModal from './components/AddNamespaceModal';
import LanguageNamespacesCard from './components/LanguageNamespacesCard';

const Project = props => {
  const dispatch = useDispatch();
  const project = useSelector(state => state.projects.project);
  const [addLanguageModalHidden, toggleAddLanguageModal] = useToggle(true);
  const [addNamespaceModalHidden, toggleAddNamespaceModal] = useToggle(true);

  // on page load, get the project
  useEffect(() => {
    dispatch(projectsActions.getProject(props.match.params.id)).catch(() =>
      toast.error(
        'Something went wrong while retrieving the project. Please try again later.'
      )
    );
  }, [dispatch, props.match.params]);

  // add a language and then reload the project after
  const handleAddLanguageSave = useCallback(
    (code, description) => {
      return dispatch(
        languagesActions.addLanguage(props.match.params.id, code, description)
      )
        .then(() => dispatch(projectsActions.getProject(props.match.params.id)))
        .catch(() =>
          toast.error(
            'Something went wrong while adding the language. Please try again later.'
          )
        );
    },
    [dispatch, props.match.params]
  );

  // add a namespace and then reload the project after
  const handleAddNamespaceSave = useCallback(
    name => {
      return dispatch(
        namespacesActions.addNamespace(props.match.params.id, name)
      )
        .then(() => dispatch(projectsActions.getProject(props.match.params.id)))
        .catch(() =>
          toast.error(
            'Something went wrong while adding the namespace. Please try again later.'
          )
        );
    },
    [dispatch, props.match.params]
  );

  // delete a language and then reload the project after
  const handleDeleteLanguage = useCallback(
    languageId => {
      return dispatch(languagesActions.deleteLanguage(languageId))
        .then(() => dispatch(projectsActions.getProject(props.match.params.id)))
        .catch(() =>
          toast.error(
            'Something went wrong while deleting the language. Please try again later.'
          )
        );
    },
    [dispatch, props.match.params]
  );

  const handleDeleteNamespace = useCallback(
    namespaceId => {
      return dispatch(namespacesActions.deleteNamespace(namespaceId))
        .then(() => dispatch(projectsActions.getProject(props.match.params.id)))
        .catch(() =>
          toast.error(
            'Something went wrong while deleting the namespace. Please try again later.'
          )
        );
    },
    [dispatch, props.match.params]
  );

  const translationOverviews =
    project &&
    Object.keys(project.languages).map(key => {
      return (
        <Col key={key} xs={12} md={6}>
          <LanguageNamespacesCard
            project={project}
            languageKey={key}
            onDeleteLanguage={handleDeleteLanguage}
            onDeleteNamespace={handleDeleteNamespace}
          />
        </Col>
      );
    });

  return (
    <Fragment>
      <PageHeader
        title={
          <Link to={`/projects/${project && project.projectId}`}>
            {project && project.projectName}
          </Link>
        }
        subtitle='Internationalization'
        actions={[
          {
            label: 'Add Language',
            icon: 'language',
            onClick: toggleAddLanguageModal,
          },
          {
            label: 'Add Namespace',
            icon: 'file',
            onClick: toggleAddNamespaceModal,
          },
          {
            label: 'Publish',
            icon: 'upload',
            onClick: () =>
              history.push(`/projects/${props.match.params.id}/publish`),
          },
        ]}
      />
      <Row>{translationOverviews}</Row>
      <AddLanguageModal
        hidden={addLanguageModalHidden}
        toggle={toggleAddLanguageModal}
        onSaveClick={handleAddLanguageSave}
      />
      <AddNamespaceModal
        hidden={addNamespaceModalHidden}
        toggle={toggleAddNamespaceModal}
        onSaveClick={handleAddNamespaceSave}
      />
    </Fragment>
  );
};

export default Project;
